import React from 'react'
import { useTranslation } from 'next-i18next'
import { Logo } from 'modules/ui/atoms/Logo'
import { ActionButton } from 'modules/ui/atoms/ActionButton'
import { usePaymentMethodSelection } from '../hooks/use-payment-method-selection'
import { translate } from '../../../../../utils/functions/translate'
import { PaymentMethodConfig } from '../utils/payment-method-configs'
import { useActivePaymentMethodConfigs } from '../utils/use-payment-method-configs'
import { PaymentMethod } from 'models/ChargeIntent'

interface PaymentMethodActionButtonProps {
  fallbackText?: string
  dataIdPrefix?: string
}

/**
 * Renders the appropriate action button for the selected payment method.
 * For actual rendering, it simply passes props down to ActionButton, however...
 *
 * This component contains logic to possibly pre-render Venmo inside a hidden div.
 *
 * 1. If Venmo is unavailable return a simple button
 * 2. If Venmo is possibly available, but not selected return a normal button & a hidden Venmo div
 * 3. If Venmo is selected, return an empty div & a proper (non-hidden) Venmo button
 *
 * ^ The behavior with the hidden div is to ensure DOM elements moving don't cause a re-render
 * (or any other complications)
 */
export const PaymentMethodActionButton = ({
  fallbackText = 'Continue',
  dataIdPrefix = 'payment-method-continue-button',
}: PaymentMethodActionButtonProps) => {
  const { t } = useTranslation()

  const { selectedPaymentMethod, selectedPaymentMethodConfig, isLoading } =
    usePaymentMethodSelection()

  // paypal and venmo can't exist at the same time, but we also need to preload venmo
  const activePaymentMethodConfigs = useActivePaymentMethodConfigs()

  const venmoConfig = activePaymentMethodConfigs.find((config) => {
    if (config.key === PaymentMethod.Venmo) {
      return config
    }
  })

  const renderVenmo =
    !!venmoConfig && selectedPaymentMethodConfig.key !== PaymentMethod.PayPal_Commerce

  const showVenmo = selectedPaymentMethodConfig.key === PaymentMethod.Venmo

  // Show loading state when payment methods are still loading
  if (isLoading) {
    return (
      <ActionButton disabled>{t('PaymentMethodsStep.footer.continue', fallbackText)}</ActionButton>
    )
  }

  const nonVenmoButton = showVenmo ? (
    <div />
  ) : (
    <_PaymentButton
      paymentMethodConfig={selectedPaymentMethodConfig}
      dataId={`${dataIdPrefix}-${selectedPaymentMethod.toLowerCase()}`}
    />
  )

  if (!renderVenmo) return nonVenmoButton
  else {
    const venmoButton = (
      <_PaymentButton
        paymentMethodConfig={venmoConfig}
        dataId={`${dataIdPrefix}-${venmoConfig.key.toLowerCase()}`}
      />
    )

    return (
      <>
        <div hidden={showVenmo}>{nonVenmoButton}</div>
        <div hidden={!showVenmo}>{venmoButton}</div>
      </>
    )
  }
}

const _PaymentButton = ({
  paymentMethodConfig,
  dataId,
}: {
  paymentMethodConfig: PaymentMethodConfig
  dataId: string
}) => {
  // Get the dynamic action button component from the config
  const ActionButton = paymentMethodConfig?.actionButton

  // Default footer labels for different payment methods
  const PaymentMethodFooterLabels: { [key: string]: string } = {
    card: translate('DonorInformationStep.footerButton.donateMessage.now', 'Continue'),
    bank_transfer: translate('DonorInformationStep.footerButton.donateMessage.now', 'Continue'),
  }

  return ActionButton ? (
    <ActionButton dataId={dataId} paymentMethodConfig={paymentMethodConfig}>
      {!paymentMethodConfig.actionButtonText && paymentMethodConfig.labelKey
        ? PaymentMethodFooterLabels[paymentMethodConfig.labelKey]
        : paymentMethodConfig.actionButtonText}
      {paymentMethodConfig?.actionButtonLogo && (
        <Logo
          name={paymentMethodConfig?.actionButtonLogo}
          altText={paymentMethodConfig?.actionButtonLogoAltText}
        />
      )}
    </ActionButton>
  ) : null
}
